import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';
import { createSelector } from "reselect";
import { uniqueByKey, memoize } from '@ls/js-utils';
import CalcType from 'state/Constants/CalcType';
import SelectionState from "state/selections/SelectionState";
import { selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedAccountEntity } from 'state/selections/selectors';
import { getEntitySummariesObject, getEntitySummariesObjectForBench } from 'state/util';
import scope from "./scope";

export const selectFactorOptions = state => state[scope].options;
export const selectData = (summaryType) => state => state[scope]['data'][summaryType];
export const selectFetchingData = (summaryType) => state => state[scope]['fetchingData'][summaryType];
export const selectTrackingSbsData = (summaryType) => (state) => state[scope]["trackingSbsData"][summaryType];
export const selectFetchingTESbsData = (summaryType) => (state) => state[scope]["fetchingTESbsData"][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return state => state[scope]['definitions'][selectionState];
  }
  return createSelector(
    state => state[scope]['definitions'][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(({ complete }) => complete);
      return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
    }
  );
});

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absData, relData, accEntityId, bmkEntityId, benchId) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          const { factor, covarianceType, calcType, /*scaleType*/ } = definition;
          const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
          const benchValue = calcType === CalcType.Benchmark ? 0 : benchId?.benchmarkId;
          const isRelative = calcType === CalcType.Relative;
          let entitySummariesObject
          if (benchValue !== null && benchValue !== undefined) {
            entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
          } else {
            entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
          }

          const trackingErrorSummary = entitySummariesObject?.trackingErrorSummaries?.find(({ primaryKey }) => primaryKey.entityId === entityId && primaryKey.covarType === covarianceType);
          row[definition.key] = trackingErrorSummary?.values?.[factor.toLowerCase()];
        });
      return rowMap;
    }, {});
  },
);

export const selectRowSbsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectTrackingSbsData(SummaryType.ABSOLUTE),
  selectTrackingSbsData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absSbsData, relSbsData, accEntityId, bmkEntityId, benchId) => {
    const absoluteSbsNodes = getFlattenedNodes(absSbsData?.root) ?? [];
    const relativeSbsNodes = getFlattenedNodes(relSbsData?.root) ?? [];
    return [...absoluteSbsNodes, ...relativeSbsNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          const { factor, covarianceType, calcType, /*scaleType*/ } = definition;
          const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
          const benchValue = calcType === CalcType.Benchmark ? 0 : benchId?.benchmarkId;
          const isRelative = calcType === CalcType.Relative;
          let entitySummariesObject
          if (benchValue !== null && benchValue !== undefined) {
            entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
          } else {
            entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
          }

          const trackingErrorSummary = entitySummariesObject?.trackingErrorSummaries?.find(({ primaryKey }) => primaryKey.entityId === entityId && primaryKey.covarType === covarianceType);
          row[`${definition.key} - scenario`] = trackingErrorSummary?.values?.[factor.toLowerCase()];
        });
      return rowMap;
    }, {});
  },
);

